import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2 as GameControllerIcon, User } from 'lucide-react';
import { GameInvitationPayload } from '@fake-stack-overflow/shared/types/socket';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import useUserContext from '../../../hooks/useUserContext';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../common/BreadcrumbNav';

/**
 * Page that displays game invitations the user has received
 */
const MyInvitationsPage: React.FC = () => {
  const { socket } = useUserContext();
  const navigate = useNavigate();

  const [invitations, setInvitations] = useState<GameInvitationPayload[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: 'Games', href: '/games' },
    { label: 'My Invitations' },
  ];

  const handleAcceptInvitation = (gameID: string) => {
    // Remove the invitation
    setInvitations(prev => prev.filter(inv => inv.gameID !== gameID));

    // Navigate to the game
    navigate(`/games/${gameID}`);
  };

  const handleDismissInvitation = (gameID: string) => {
    // Just remove the invitation
    setInvitations(prev => prev.filter(inv => inv.gameID !== gameID));
  };

  function renderContent() {
    if (loading) {
      return (
        <div className='flex justify-center p-8'>
          <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
        </div>
      );
    }

    if (invitations.length > 0) {
      return (
        <div className='space-y-4'>
          {invitations.map(invitation => (
            <Card key={invitation.gameID} className='bg-gray-50'>
              <CardContent className='p-4'>
                <div className='flex justify-between items-start'>
                  <div className='space-y-2'>
                    <div className='flex items-center space-x-2'>
                      <User className='h-4 w-4 text-gray-500' />
                      <span className='font-medium'>{invitation.fromUsername}</span>
                    </div>
                    <div className='flex items-center space-x-2'>
                      <GameControllerIcon className='h-4 w-4 text-gray-500' />
                      <span>{invitation.gameType} Game</span>
                    </div>
                  </div>
                  <div className='flex space-x-2'>
                    <Button
                      variant='default'
                      size='sm'
                      onClick={() => handleAcceptInvitation(invitation.gameID)}
                      className='bg-blue-600 hover:bg-blue-700'>
                      Join Game
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => handleDismissInvitation(invitation.gameID)}>
                      Dismiss
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    return (
      <Alert className='bg-gray-50 border-gray-200'>
        <AlertDescription>
          You don&apos;t have any active game invitations. When someone invites you to a game, it
          will appear here.
        </AlertDescription>
      </Alert>
    );
  }

  useEffect(() => {
    // Create a cleanup function outside the conditional
    const cleanup = () => {
      if (socket) {
        socket.off('gameInvitation');
      }
    };

    // Early return pattern with cleanup
    if (!socket) {
      setLoading(false);
      return cleanup;
    }

    const handleGameInvitation = (invitation: GameInvitationPayload) => {
      setInvitations(prev => [...prev, invitation]);
    };

    setLoading(false);
    socket.on('gameInvitation', handleGameInvitation);

    return cleanup;
  }, [socket]);

  return (
    <div className='container mx-auto p-6'>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbItems} />

      <Card>
        <CardHeader>
          <div className='flex items-center space-x-2'>
            <GameControllerIcon className='h-6 w-6 text-blue-600' />
            <div>
              <CardTitle>My Game Invitations</CardTitle>
              <CardDescription>
                View and manage games you&apos;ve been invited to join
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>{renderContent()}</CardContent>
      </Card>
    </div>
  );
};

export default MyInvitationsPage;
